<template>
  <div class="for-transition w-100">
    <!-- Phone and Email -->
    <div class="bg-white mn-b-thin">
      <h3 class="mn-b-small">{{ !(core.state?.options?.auth?.authMethodsExclude || []).includes('phone') ? 'Phone and Email' : 'Email' }}</h3>

      <!-- Phone -->
      <div v-if="!(core.state?.options?.auth?.authMethodsExclude || []).includes('phone')" class="mn-b-semi">
        <Field
          v-if="users.state.current.phone && !isEditingPhone"
          v-model:field="users.state.current.phone"
          label="Phone"
          class="mn-b-thin bg-light pd-medium radius-small"
          disabled
        />
        <FieldPhone
          v-else
          @change="(val) => newPhone = val"
          :dropdownOptions="{
            showDialCodeInSelection: true,
            showFlags: true,
            showDialCodeInList: true
          }"
          :validCharactersOnly="true"
          mode="national"
          :inputOptions="{ placeholder: 'Enter phone number', type: 'tel' }"
          class="bg-light h-4r pd-small radius-small mn-b-thin"
        />
        <Button
          v-if="users.state.current.phone && !isEditingPhone"
          :submit="() => isEditingPhone = true"
          class="mn-b-thin t-black bg-light w-100"
        >
          Change Phone
        </Button>
        <div v-else-if="!users.state.current.phone" class="flex gap-1">
          <Button
            :submit="() => savePhone()"
            class="mn-b-thin bg-main w-100"
          >
            Save Phone
          </Button>
        </div>
        <div v-else class="flex gap-2">
          <Button
            :submit="() => savePhone()"
            class="mn-b-thin bg-main flex-child-1"
          >
            Save Phone
          </Button>
          <Button
            :submit="() => { isEditingPhone = false; newPhone = '' }"
            class="mn-b-thin bg-red t-white flex-child-1"
          >
            Cancel
          </Button>
        </div>
      </div>

      <!-- Email -->
      <div class="mn-b-semi">
        <Field
          v-if="users.state.current.email && !isEditingEmail"
          v-model:field="users.state.current.email"
          label="Email"
          class="mn-b-thin bg-light pd-medium radius-small"
          disabled
        />
        <Field
          v-else
          v-model:field="newEmail"
          label="Email"
          placeholder="Enter email"
          class="mn-b-thin bg-light pd-medium radius-small"
        />
        <Button
          v-if="users.state.current.email && !isEditingEmail"
          :submit="() => isEditingEmail = true"
          class="mn-b-thin t-black bg-light w-100"
        >
          Change Email
        </Button>
        <div v-else-if="!users.state.current.email" class="flex gap-1">
          <Button
            :submit="() => saveEmail()"
            class="mn-b-thin bg-main w-100"
          >
            Save Email
          </Button>
        </div>
        <div v-else class="flex gap-2">
          <Button
            :submit="() => saveEmail()"
            class="mn-b-thin bg-main flex-child-1"
          >
            Save Email
          </Button>
          <Button
            :submit="() => { isEditingEmail = false; newEmail = '' }"
            class="mn-b-thin bg-red t-white flex-child-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>

    <!-- Settings -->
    <Block title="Settings" class="mn-b-thin">
      <div class="flex flex-column gap-thin">
        <Checkbox
          :label="'Darkmode'"
          :radio="ds.theme.mode === 'dark'"
          @update:radio="() => ds.theme.toggle()"
          class="w-100 bg-white t-black pd-medium radius-small"
        />

        <Button
          :submit="onSubmit"
          :callback="redirectTo"
          class="bg-main w-100"
        >
          Save
        </Button>
      </div>
    </Block>

    <!-- Account Actions -->
    <Block title="Account" class="mn-b-thin">
      <div class="flex flex-column gap-thin">
        <Button
          :submit="logout"
          class="w-100 bg-white t-red br-1px br-light radius-small"
        >
          Logout
        </Button>

        <Button
          :submit="onSubmitDelete"
          :callback="() => router.push({ name: 'Sign In' })"
          class="w-100 bg-red t-white radius-small"
        >
          Delete Account
        </Button>
      </div>
    </Block>
  </div>
</template>

<script setup>
import Checkbox from '@martyrs/src/design-system/components/Checkbox/Checkbox.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'
import Block from '@martyrs/src/design-system/components/Block/Block.vue'
import { ds } from '@martyrs/src/design-system/index.js'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'

import { ref } from 'vue'
import { useRouter } from 'vue-router'

import * as auth from '@martyrs/src/modules/auth/views/store/auth.js'
import * as users from '@martyrs/src/modules/auth/views/store/users.js'
import { useStore, setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { withOtp } from '@martyrs/src/modules/auth/views/plugins/otp.plugin.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'

const store = useStore()
const core = store.core
const router = useRouter()

const isEditingPhone = ref(false)
const isEditingEmail = ref(false)
const newPhone = ref('')
const newEmail = ref('')

defineProps({
  userData: {
    type: Object,
    required: true
  }
})

async function savePhone() {
  if (!newPhone.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/phone`, data),
      { phone: newPhone.value }
    )

    if (result.data) {
      users.state.current.phone = result.data.phone
    }

    isEditingPhone.value = false
    newPhone.value = ''
  } catch (error) {
    console.log('savePhone catch:', error)
    setError(error)
  }
}

async function saveEmail() {
  if (!newEmail.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/email`, data),
      { email: newEmail.value }
    )

    if (result.data) {
      users.state.current.email = result.data.email
    }

    isEditingEmail.value = false
    newEmail.value = ''
  } catch (error) {
    setError(error)
  }
}

async function onSubmit() {
  await users.actions.update(users.state.current)
}

async function onSubmitDelete() {
  const confirmation = window.confirm("Are you sure you want to delete your account? Restoration will be possible only with the help of an administrator.")

  if (!confirmation) return

  users.state.current.status = 'removed'
  await users.actions.update(users.state.current)
  auth.actions.logout()
}

function logout() {
  auth.actions.logout()
  router.push({ name: 'Sign In' })
}

function redirectTo() {
  router.back()
}
</script>
